/**
 * LifecycleRail — rend VISIBLE et NAVIGABLE la suite du cycle : ce que devient une idée
 * (cadrage → options → spécification → backlog → code → publication). L'étape courante
 * est mise en avant ; les précédentes sont « faites », les suivantes « à venir ». Chaque
 * étape est un lien vers l'écran où agir. Répond au « je ne vois pas ce que devient
 * l'idée ensuite » ET au « je ne sais pas où aller ».
 *
 * LES ÉTAPES SE PASSENT, ELLES NE SE LISENT PAS
 * ---------------------------------------------
 * Le rail reçoit ses étapes en PROPRIÉTÉ et retombe sur le cycle intégré quand on ne lui en
 * donne pas. Il pourrait les résoudre lui-même depuis le template de l'espace actif, mais il
 * dépendrait alors du contexte d'espace et de React Query — deux providers qu'un rail
 * d'onboarding n'a aucune raison d'exiger, et sans lesquels il cesserait de se rendre. C'est
 * l'appelant, qui a déjà ces providers, qui résout et transmet.
 */
import type { JSX } from 'react'
import { Link } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'

import { useT } from '@/i18n/language'
import { cn } from '@/lib/utils'
import { type LifecycleStep } from '@/lib/lifecycle'
import { etapesEffectives, iconeDEtape, type EtapeCycleDeVie } from '@/lib/lifecycleTemplate'

export type { LifecycleStep }

export function LifecycleRail({ current, etapes, className }: {
  /** Clé de l'étape courante. Une clé absente des étapes n'en marque aucune — jamais d'erreur. */
  current: LifecycleStep | string
  /** Étapes à afficher ; absentes → le cycle intégré (`lib/lifecycle.ts`). */
  etapes?: readonly EtapeCycleDeVie[]
  className?: string
}): JSX.Element {
  const { t } = useT()
  const liste = etapes && etapes.length > 0 ? etapes : etapesEffectives(undefined)
  const idx = liste.findIndex((s) => s.key === current)

  return (
    <ol className={cn('flex flex-wrap items-center gap-1 text-xs', className)}
        aria-label={t('lifecycleRail.aria', 'Étapes du cycle de vie')}>
      {liste.map((s, i) => {
        const etat = i < idx ? 'done' : i === idx ? 'current' : 'todo'
        const Icone = iconeDEtape(s)
        // Un libellé venu du template fait AUTORITÉ : le repasser par la clé canonique
        // réafficherait « Spécification » là où l'organisation a écrit autre chose.
        const libelle = s.duTemplate ? s.label : t(`lifecycleRail.${s.key}`, s.label)
        const classes = cn(
          'inline-flex items-center gap-1 rounded-full border px-2 py-1 transition-colors',
          etat === 'current' && 'border-primary bg-primary/10 font-medium text-brand-text',
          etat === 'done' && 'text-muted-foreground',
          // Une étape à venir se distingue par sa BORDURE POINTILLÉE, pas par un texte
          // délavé : `text-muted-foreground/60` tombait à 2,85:1 de contraste, sous le
          // minimum AA de 4,5:1 — un libellé qu'on ne lit plus n'informe plus de rien.
          etat === 'todo' && 'border-dashed text-muted-foreground',
        )
        return (
          <li key={s.key} className="flex items-center gap-1">
            {s.to ? (
              <Link to={s.to}
                    aria-current={etat === 'current' ? 'step' : undefined}
                    className={cn(classes,
                      'hover:bg-accent hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring')}>
                <Icone className="size-3" aria-hidden="true" />
                {libelle}
              </Link>
            ) : (
              // Étape déclarée sans route : elle reste VISIBLE dans le parcours mais n'est pas
              // un lien. Un lien vers nulle part serait pire qu'une pastille inerte.
              <span aria-current={etat === 'current' ? 'step' : undefined} className={classes}>
                <Icone className="size-3" aria-hidden="true" />
                {libelle}
              </span>
            )}
            {i < liste.length - 1 ? (
              <ChevronRight className="size-3 text-muted-foreground/40" aria-hidden="true" />
            ) : null}
          </li>
        )
      })}
    </ol>
  )
}
